import { CheckCircleOutline, DraftsOutlined, PeopleOutline } from '@mui/icons-material';
import { Alert, Box, Button, CircularProgress, Grid, Stack, Tooltip, Typography, lighten } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Colors } from 'src/shared/components/styles/colors';
import { GroupMessageResponse } from '../models/groupMessageResponse';
import { GroupMessageEmailStatusModal } from './groupMessageEmailStatusModal';

type GroupMessageEmailStatusMessageProps = {
  callback: () => void;
  groupMessageEmailResponse: GroupMessageResponse;
};

type StatCardProps = {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
  iconColor?: 'info' | 'success';
};

const StatCard = ({ icon, label, children, iconColor = 'info' }: StatCardProps) => (
  <Box sx={{ flex: '0 0 auto', minWidth: 0, display: 'flex', alignItems: 'center', gap: 1.25 }}>
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: (thm) => lighten(thm.palette[iconColor].main, 0.85),
        color: `${iconColor}.main`,
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography variant='body2' sx={{ display: 'block', color: 'text.secondary', lineHeight: 1.2 }}>
        {label}
      </Typography>
      {children}
    </Box>
  </Box>
);

export const GroupMessageEmailStatusMessage = (props: GroupMessageEmailStatusMessageProps) => {
  const { callback, groupMessageEmailResponse } = props;
  const { t } = useTranslation(['groupMessage']);

  const [showSmsStatusModal, setShowSmsStatusModal] = useState<boolean>(false);

  const handleSmsStatus = () => {
    setShowSmsStatusModal(false);
    callback();
  };

  const recipientCount = groupMessageEmailResponse?.recipientCount ?? 0;
  const openedCount = groupMessageEmailResponse?.openedCount ?? 0;
  const openedPct = recipientCount > 0 ? Math.round((openedCount / recipientCount) * 100) : 0;
  let ringColor = 'error.main';
  if (openedPct >= 40) ringColor = 'success.main';
  else if (openedPct >= 20) ringColor = 'warning.main';

  return (
    <Grid item xs={12}>
      <Alert
        severity='info'
        variant='outlined'
        icon={false}
        sx={{
          backgroundColor: lighten(Colors.ChipColors.Blue, 0.9),
          '& .MuiAlert-message': { width: '100%', p: 0 },
          p: 2,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'stretch', md: 'center' },
            gap: 2,
          }}
        >
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1.5, sm: 3 }} sx={{ minWidth: 0 }}>
            <StatCard icon={<CheckCircleOutline />} label={t('groupMessage:sms.sent')} iconColor='success'>
              <Typography variant='body1' sx={{ fontWeight: 600, lineHeight: 1.2 }} noWrap>
                {groupMessageEmailResponse?.sendDate}
              </Typography>
            </StatCard>
            {recipientCount > 0 && (
              <StatCard icon={<PeopleOutline />} label='Antal mottagare'>
                <Typography variant='body1' sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                  {recipientCount} st
                </Typography>
              </StatCard>
            )}
            {recipientCount > 0 && (
              <Box sx={{ flex: '0 0 auto', minWidth: 0, display: 'flex', alignItems: 'center', gap: 1.25 }}>
                <Tooltip title={`${openedCount} av ${recipientCount} öppnade (${openedPct}%)`} arrow>
                  <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <CircularProgress variant='determinate' value={100} size={44} thickness={4} sx={{ color: 'grey.300', position: 'absolute' }} />
                    <CircularProgress variant='determinate' value={openedPct} size={44} thickness={4} sx={{ color: ringColor }} />
                    <Box sx={{ position: 'absolute', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <DraftsOutlined sx={{ fontSize: '1.1rem', color: ringColor }} />
                    </Box>
                  </Box>
                </Tooltip>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant='body2' sx={{ display: 'block', color: 'text.secondary', lineHeight: 1.2 }}>
                    Öppningsgrad
                  </Typography>
                  <Typography variant='body1' sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                    {openedCount}/{recipientCount}{' '}
                    <Typography component='span' variant='body2' sx={{ color: 'text.secondary', fontWeight: 500 }}>
                      ({openedPct}%)
                    </Typography>
                  </Typography>
                </Box>
              </Box>
            )}
          </Stack>
          <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'block' } }} />
          {groupMessageEmailResponse?.circulars.length > 0 && (
            <Button
              variant='contained'
              size='large'
              disableElevation
              onClick={() => setShowSmsStatusModal(true)}
              sx={{ width: { xs: '100%', md: 'auto' }, flexShrink: 0 }}
            >
              {t('groupMessage:sms.deliveryStatus')}
            </Button>
          )}
        </Box>
      </Alert>
      {showSmsStatusModal && (
        <GroupMessageEmailStatusModal
          modalOpen={showSmsStatusModal}
          groupMessageEmailResponse={groupMessageEmailResponse}
          onSave={() => handleSmsStatus()}
          onClose={() => handleSmsStatus()}
        />
      )}
    </Grid>
  );
};
